import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToSection } from "./Navbar";
import { useLang } from "../context/LanguageContext";
import usePhotos from "../hooks/usePhotos";

const HERO_IMG =
  "https://customer-assets-0z36b82j.emergentagent.net/job_casale-tradizione/artifacts/043031d50d79b532_e429301f-f6dc-473c-acf1-4bebda3e8851.jpeg";

const LINES = {
  it: [
    { text: "Tradizione e innovazione", accent: false },
    { text: "ritornano a tavola", accent: true },
    { text: "a Casale Monferrato", accent: false },
  ],
  en: [
    { text: "Tradition and innovation", accent: false },
    { text: "return to the table", accent: true },
    { text: "in Casale Monferrato", accent: false },
  ],
};

export default function Hero() {
  const { lang } = useLang();
  const photos = usePhotos();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={photos.hero || HERO_IMG}
          alt="Sala del Ristorante Milano"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0C0A]/70 via-[#0D0C0A]/40 to-[#0D0C0A]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0D0C0A]/90 via-[#0D0C0A]/45 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(13,12,10,0.55)_100%)]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24 w-full"
      >
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          data-testid="hero-heritage-badge"
          className="inline-flex items-center gap-3 border border-[rgba(201,162,101,0.4)] rounded-full px-5 py-2 mb-10 bg-[#0D0C0A]/60"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#C9A265] animate-pulse" />
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#C9A265] font-cinzel">
            Piazza Castello 41 · Casale Monferrato (AL)
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-[#FAF7F2] [text-shadow:0_2px_28px_rgba(13,12,10,0.9),0_0_6px_rgba(13,12,10,0.7)]"
        >
          {LINES[lang].map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.accent ? "italic text-[#C9A265]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{
                  delay: 0.45 + i * 0.18,
                  duration: 0.9,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#E8E2D8] leading-relaxed bg-[#0D0C0A]/70 rounded-2xl px-6 py-5 border border-[rgba(201,162,101,0.15)]"
        >
          {lang === "it"
            ? "Portati dalla nostra passione per la cucina, abbiamo deciso di riaprire lo storico Ristorante Milano, situato nella piazza centrale della città, dopo più di trentacinque anni di inattività — con un'idea di cucina che rispetta la tradizione del territorio, ma volta a dare un tocco di innovazione."
            : "Driven by our passion for cooking, we decided to reopen the historic Ristorante Milano, set in the city's central square, after more than thirty-five years of inactivity — with a cuisine that respects the territory's tradition while adding a touch of innovation."}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.8 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-reserve-btn"
            onClick={() => scrollToSection("contatti")}
            className="px-8 py-4 bg-[#C9A265] text-[#0D0C0A] text-xs uppercase tracking-[0.25em] font-semibold font-cinzel rounded-full hover:bg-[#E0B87A] hover:shadow-[0_0_40px_rgba(201,162,101,0.35)] transition-all duration-300"
          >
            {lang === "it" ? "Prenota un Tavolo" : "Book a Table"}
          </button>
          <button
            data-testid="hero-menu-btn"
            onClick={() => scrollToSection("menu")}
            className="px-8 py-4 border border-[rgba(250,247,242,0.3)] text-[#FAF7F2] text-xs uppercase tracking-[0.25em] font-cinzel rounded-full hover:border-[#C9A265] hover:text-[#C9A265] transition-colors duration-300"
          >
            {lang === "it" ? "Scopri il Menù" : "Discover the Menu"}
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#7A7267] font-cinzel">
          {lang === "it" ? "Scorri" : "Scroll"}
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-10 bg-gradient-to-b from-[#C9A265] to-transparent"
        />
      </motion.div>
    </section>
  );
}
